import React from "react";
import { Link } from "react-router-dom";
import PublicLayout, { IHPL_BLUE } from "./PublicLayout";
import { Phone, ArrowRight, Wrench, ShieldCheck, Truck, Users, Clock, MapPin, ChevronRight, CheckCircle2, Factory, HardHat, Cog, Anchor, Zap, Package } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/36237165/pexels-photo-36237165.jpeg?auto=compress&cs=tinysrgb&w=2000";

const PILLARS = [
  { icon: Package, title: "Products", body: "Full range of SAE, EN hoses, fittings and assemblies — engineered to your spec.", link: "/products", color: "bg-[#004E9B]" },
  { icon: Wrench, title: "Services", body: "Custom assembly · On-site emergency service · Hose testing · Bulk supply for OEMs.", link: "/services", color: "bg-[#0A6EBD]" },
  { icon: Factory, title: "Industries", body: "Powering India's mines, steel plants, cement kilns, ports and construction fleets.", link: "/industries", color: "bg-[#003268]" },
];

const INDUSTRY_STRIP = [
  { icon: HardHat, label: "Mining & Earthmoving" },
  { icon: Factory, label: "Steel & Metals" },
  { icon: Cog, label: "Cement & Aggregates" },
  { icon: Anchor, label: "Ports & Marine" },
  { icon: Truck, label: "Construction" },
  { icon: Zap, label: "Power & Energy" },
];

const COVERAGE = [
  { state: "Uttar Pradesh", cities: "Prayagraj · Kanpur · Lucknow · Varanasi" },
  { state: "Jharkhand", cities: "Jamshedpur · Ranchi · Bokaro · Dhanbad" },
  { state: "Chhattisgarh", cities: "Raipur · Bhilai · Korba · Bilaspur" },
  { state: "Madhya Pradesh", cities: "Bhopal · Indore · Jabalpur · Singrauli" },
  { state: "Odisha", cities: "Bhubaneswar · Rourkela · Angul · Paradip" },
];

const WHY = [
  { icon: ShieldCheck, title: "ISO 9001:2015", body: "Every assembly documented, batch-traceable, audit-ready." },
  { icon: Clock, title: "48-Hour Dispatch", body: "Standard SKUs shipped within 48 hours across our coverage zone." },
  { icon: Truck, title: "On-Site Emergency", body: "Field crews for 2 AM breakdowns at mines and plants." },
  { icon: Users, title: "Real Engineers", body: "Not call-centres. You talk to someone who knows hoses." },
];

export default function Home() {
  return (
    <PublicLayout>
      {/* ============ HERO ============ */}
      <section className="relative bg-[#0A2540] text-white overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
        </div>
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,78,155,0.95) 0%, rgba(0,78,155,0.75) 55%, rgba(10,37,64,0.4) 100%)" }} />
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F5B301] text-[#0A2540] text-xs font-bold uppercase tracking-wider rounded-sm" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 bg-[#0A2540] rounded-full" /> Mobile & Workshop Hydraulic Hose Service
            </div>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="hero-h1">
              Hydraulic hoses<br /> and fittings.<br />
              <span className="text-[#F5B301]">On-site. On time.</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed" data-testid="hero-sub">
              From SAE 100R assemblies to bespoke high-pressure hoses — designed, tested and dispatched from Prayagraj to India's toughest job sites.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="tel:+918962913616" className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#E11D2A] hover:bg-[#B01722] text-white font-bold text-sm uppercase tracking-wider rounded-sm transition-colors" data-testid="hero-emergency">
                <Phone size={16} /> Emergency · +91 89629 13616
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-[#0A2540] hover:bg-slate-100 font-bold text-sm uppercase tracking-wider rounded-sm transition-colors" data-testid="hero-quote">
                Get a Quote <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-md p-6" data-testid="hero-locator-card">
              <div className="text-xs font-bold uppercase tracking-wider text-[#F5B301] mb-2">Coverage Zone</div>
              <div className="font-display font-black text-2xl mb-4">Central & East India</div>
              <div className="space-y-2 text-sm">
                {COVERAGE.slice(0, 3).map(c => (
                  <div key={c.state} className="flex items-start gap-2 text-white/85"><MapPin size={14} className="mt-0.5 text-[#F5B301] shrink-0" /> <span><strong className="text-white">{c.state}</strong> — {c.cities}</span></div>
                ))}
                <div className="text-xs text-white/60 pt-1">+ MP & Odisha (see full coverage below)</div>
              </div>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[#F5B301]">
                Not in this zone? Contact us <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 3-TILE PILLARS ============ */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 -mt-8 relative z-10" data-testid="home-pillars">
        <div className="grid md:grid-cols-3 gap-4">
          {PILLARS.map(p => {
            const Icon = p.icon;
            return (
              <Link key={p.title} to={p.link} className={`group ${p.color} text-white p-8 rounded-md hover:shadow-2xl transition-shadow`} data-testid={`pillar-${p.title.toLowerCase()}`}>
                <Icon size={38} strokeWidth={1.5} className="mb-4" />
                <div className="font-display font-black text-2xl uppercase tracking-tight mb-2">{p.title}</div>
                <p className="text-white/85 text-sm leading-relaxed">{p.body}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider group-hover:gap-2.5 transition-all">
                  Learn more <ArrowRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ============ INDUSTRY STRIP ============ */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20" data-testid="home-industry-strip">
        <div className="text-center mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-[#004E9B] mb-2">Industries We Serve</div>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight text-slate-900">Built for heavy industry.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {INDUSTRY_STRIP.map(i => {
            const Icon = i.icon;
            return (
              <Link key={i.label} to="/industries" className="flex flex-col items-center gap-3 p-5 bg-slate-50 hover:bg-[#004E9B] hover:text-white text-slate-700 rounded-md transition-colors text-center group" data-testid={`industry-${i.label.replace(/\W/g, "").toLowerCase()}`}>
                <Icon size={32} strokeWidth={1.5} className="text-[#004E9B] group-hover:text-[#F5B301] transition-colors" />
                <div className="text-xs font-bold uppercase tracking-wider leading-tight">{i.label}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ============ EMERGENCY BAND ============ */}
      <section className="bg-[#E11D2A] text-white" data-testid="home-emergency-band">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-3 text-white/80">24/7 Emergency Hose Service</div>
            <h2 className="font-display font-black text-3xl md:text-5xl leading-tight tracking-tight">Hose burst at 2 AM?<br /> We'll be there.</h2>
            <p className="mt-4 text-white/90 text-lg max-w-lg">Field crews on standby for mines, ports and plants across our coverage zone. Day or night.</p>
          </div>
          <div className="flex md:justify-end">
            <a href="tel:+918962913616" className="inline-flex items-center gap-3 px-7 py-5 bg-white text-[#E11D2A] font-black text-lg uppercase tracking-wider rounded-sm hover:bg-slate-100 transition-colors" data-testid="emergency-band-call">
              <Phone size={22} /> +91 89629 13616
            </a>
          </div>
        </div>
      </section>

      {/* ============ WHY IHPL ============ */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20" data-testid="home-why">
        <div className="text-center mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-[#004E9B] mb-2">Why Indus Hoses</div>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight text-slate-900">Fifteen years of high pressure.</h2>
          <p className="mt-4 text-slate-600 text-lg max-w-2xl mx-auto">One product, done extremely well. No side hustles, no distractions — just hydraulic hose assemblies that survive.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY.map(w => {
            const Icon = w.icon;
            return (
              <div key={w.title} className="p-6 border border-slate-200 rounded-md hover:border-[#004E9B] hover:shadow-md transition-all" data-testid={`why-${w.title.replace(/\W/g, "").toLowerCase()}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#004E9B]/10 text-[#004E9B] rounded-md mb-4">
                  <Icon size={22} />
                </div>
                <div className="font-display font-black text-lg text-slate-900 mb-2">{w.title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{w.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ COVERAGE MAP ============ */}
      <section className="bg-slate-50" data-testid="home-coverage">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-widest text-[#004E9B] mb-2">Service Coverage</div>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight text-slate-900 leading-tight">5 states.<br /> One phone call.</h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">Dispatched from Prayagraj to your site — usually within 48 hours for stocked SKUs.</p>
            <Link to="/contact" className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#004E9B] hover:bg-[#003268] text-white font-bold text-sm uppercase tracking-wider rounded-sm transition-colors" data-testid="coverage-cta">
              Check your location <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 space-y-2">
            {COVERAGE.map(c => (
              <div key={c.state} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-md hover:border-[#004E9B] transition-colors" data-testid={`coverage-${c.state.replace(/\W/g, "").toLowerCase()}`}>
                <MapPin size={20} className="text-[#004E9B] mt-0.5 shrink-0" />
                <div>
                  <div className="font-display font-black text-slate-900">{c.state}</div>
                  <div className="text-sm text-slate-600 mt-0.5">{c.cities}</div>
                </div>
                <CheckCircle2 size={18} className="text-emerald-500 ml-auto mt-0.5 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-[#004E9B] text-white" data-testid="home-final-cta">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 grid md:grid-cols-2 gap-8 items-center">
          <h2 className="font-display font-black text-3xl md:text-5xl leading-tight tracking-tight">
            Send us the drawing.<br /> We'll <span className="text-[#F5B301]">build the hose.</span>
          </h2>
          <div className="flex md:justify-end flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 bg-[#F5B301] text-[#0A2540] hover:bg-yellow-400 font-black text-sm uppercase tracking-wider rounded-sm transition-colors" data-testid="final-quote">
              Start an Inquiry <ArrowRight size={16} />
            </Link>
            <a href="https://wa.me/918962913616" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-4 bg-white text-[#004E9B] hover:bg-slate-100 font-black text-sm uppercase tracking-wider rounded-sm transition-colors" data-testid="final-whatsapp">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
